import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';

import { compose, identity } from 'lodash/fp';

import {
    TableCell as BaseTableCell,
    FlexBox,
    H3,
    Pagination,
    RowsPerPageSelector,
    Table,
    TableBody,
    TableEmptyText,
    TableHead,
    TableRow,
} from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { DataCondition, DataFilters, DataKey, DataResponse, DataType } from '@portal/common/types';
import { localizeColumns, localizeRecords } from '@portal/common/utility/chart-data-helpers';

import _ from '../../../locale';
import { isValidDataResponse } from '../../../models/data-response';
import usePrevious from '../../../utility/hook/use-previous';
import Headers from './Headers';
import Rows from './Rows';
import { filterDataResponse, getRecordValuesByIdxs, sortRawTableData } from './utils';

const LoadingLabel = styled(H3)({
    textAlign: 'center',
});

const Spacer = styled('div')({ flexGrow: 1, height: 70 });

const TableCell = styled(BaseTableCell)(({ theme }) => ({
    padding: '4px 0 4px 4px',
    fontSize: '1.3rem',
    [theme.breakpoint.md]: {
        padding: '4px 4px 4px 10px',
        fontSize: '1.55rem',
    },
}));

const StyledPagination = styled(Pagination)({
    flex: 1,
});

const StyledTable = styled(Table)({
    minWidth: '100%',
});

const TableContainer = styled('div')(({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
}));

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

type Props = {
    data?: DataResponse;
    valueFields: string[];
    onDisplayChange: ({ sortedByColumn, sortedDescending }) => void;
    onDataRendered: () => void;
    preprocessTableData: (data: DataResponse) => DataResponse;
    ageGroupsOrder: any;
    selectedRefinementFilters: DataFilters;
    selectedConditions: DataCondition[];
    dataResponse: DataResponse;
    dataTypes: DataType[];
    isLoadingData: boolean;
    isForecastingData: boolean;
    visibleColumns: DataKey[];
    sortedByColumn: DataKey | null;
    sortedDescending: boolean;
    showDecimals: boolean;
    className?: string;
};

type PaginationState = {
    page: number;
    rowsPerPage: number;
};

const defaultPagination = {
    page: 0,
    rowsPerPage: 5,
};

const CustomDataTable = (
    {
        valueFields = [],
        onDisplayChange = identity,
        onDataRendered,
        preprocessTableData = identity,
        ageGroupsOrder,
        selectedRefinementFilters,
        selectedConditions,
        dataResponse,
        dataTypes,
        isLoadingData,
        visibleColumns,
        sortedByColumn,
        sortedDescending,
        showDecimals = true,
        className,
    },
    ref
) => {
    const previousDataResponse = usePrevious(dataResponse);
    const [pagination, setPagination] = useState<PaginationState>(defaultPagination);

    const preprocessedData = useMemo(() => {
        if (!dataResponse) {
            return {
                columns: [],
                records: [],
            };
        }

        const sortParams = { sortedByColumn, sortedDescending, ageGroupsOrder };

        return compose(preprocessTableData, sortRawTableData(sortParams))(dataResponse);
    }, [dataResponse, sortedByColumn, sortedDescending, ageGroupsOrder, preprocessTableData]);

    const getVisibleColumnsIdxs = useCallback(
        (columns): number[] => (visibleColumns || []).map((column) => columns.indexOf(column)),
        [visibleColumns]
    );

    const filteredColumns = useMemo(
        () =>
            getRecordValuesByIdxs(
                preprocessedData.columns,
                getVisibleColumnsIdxs(preprocessedData.columns)
            ),
        [getVisibleColumnsIdxs, preprocessedData.columns]
    );
    const validatedPage = useMemo(() => {
        const { page, rowsPerPage } = pagination;
        const maxPage = Math.ceil(preprocessedData.records.length / rowsPerPage);

        let validatedPage = page;
        if (page > maxPage) {
            validatedPage = 0;
        }
        return validatedPage;
    }, [preprocessedData.records, pagination]);

    const visibleRecords = useMemo(() => {
        const { rowsPerPage } = pagination;

        return preprocessedData.records.slice(
            validatedPage * rowsPerPage,
            validatedPage * rowsPerPage + rowsPerPage
        );
    }, [preprocessedData.records, validatedPage, pagination.rowsPerPage]);

    const remainingEmptyRows = useMemo(() => {
        const { rowsPerPage } = pagination;
        return (
            rowsPerPage -
            Math.min(rowsPerPage, preprocessedData.records.length - validatedPage * rowsPerPage)
        );
    }, [preprocessedData.records, validatedPage, pagination.rowsPerPage]);
    const shouldRemainingRowsBeShown = useMemo(
        () => !!preprocessedData.records.length && remainingEmptyRows > 0,
        [preprocessedData.records, remainingEmptyRows]
    );

    const handleChangePage = useCallback(
        (page) => setPagination({ ...pagination, page: page - 1 }),
        [pagination]
    );

    const handleRowsPerPageChange = useCallback(
        (ev) => {
            const { rowsPerPage, page } = pagination;
            const newRowsPerPage = ev.target.value;

            const firstRowNumber = rowsPerPage * page + 1;
            const newPage = Math.floor(firstRowNumber / newRowsPerPage);

            setPagination({
                rowsPerPage: newRowsPerPage,
                page: newPage,
            });
        },
        [pagination]
    );

    const getFullData = useCallback((): DataResponse => {
        const { columns, records } = preprocessedData;
        const localizedData = {
            columns: localizeColumns(columns, dataTypes, _),
            records: localizeRecords(columns, records, _),
        };

        return filterDataResponse(localizedData, getVisibleColumnsIdxs(columns), true);
    }, [preprocessedData, dataTypes, getVisibleColumnsIdxs]);

    useImperativeHandle(ref, () => ({
        getFullData,
    }));

    useEffect(() => {
        if (
            !isValidDataResponse(previousDataResponse) &&
            isValidDataResponse(dataResponse) &&
            !isLoadingData
        ) {
            onDataRendered && onDataRendered();
        }
    }, [onDataRendered, dataResponse, previousDataResponse, isLoadingData]);

    if (isLoadingData) {
        return <LoadingLabel>Loading...</LoadingLabel>;
    }
    const { columns, records } = preprocessedData;

    return (
        <React.Fragment>
            <TableContainer>
                <StyledTable stripes className={className}>
                    <TableHead>
                        <Headers
                            {...{
                                columns: filteredColumns,
                                sortedByColumn,
                                sortedDescending,
                                onDisplayChange,
                                dataTypes,
                                selectedConditions,
                                selectedRefinementFilters,
                                valueFields,
                            }}
                        />
                    </TableHead>
                    <TableBody>
                        {visibleRecords.length ? (
                            <Rows
                                columns={columns}
                                records={visibleRecords}
                                page={validatedPage}
                                getVisibleColumnsIdxs={getVisibleColumnsIdxs}
                                showDecimals={showDecimals}
                                valueFields={valueFields}
                            />
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <TableEmptyText>{_('data_table_error_no_data')}</TableEmptyText>
                                </TableCell>
                            </TableRow>
                        )}
                        {shouldRemainingRowsBeShown &&
                            Array(remainingEmptyRows)
                                .fill(undefined)
                                .map((__, idx) => (
                                    <TableRow key={idx + 'missing'}>
                                        <TableCell
                                            colSpan={columns.length}
                                            style={{
                                                borderBottomColor: 'transparent',
                                            }}
                                        />
                                    </TableRow>
                                ))}
                    </TableBody>
                </StyledTable>
            </TableContainer>
            {records.length > 0 && (
                <FlexBox style={{ position: 'relative' }}>
                    <RowsPerPageSelector
                        label={_('data_table_rows_per_page')}
                        options={ROWS_PER_PAGE_OPTIONS}
                        value={pagination.rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        style={{
                            position: 'absolute',
                            paddingTop: records.length > pagination.rowsPerPage ? 12 : 20,
                        }}
                    />
                    {records.length > pagination.rowsPerPage ? (
                        <StyledPagination
                            totalItems={records.length}
                            currentPage={validatedPage + 1}
                            onChangePage={handleChangePage}
                            rowsPerPage={pagination.rowsPerPage}
                        />
                    ) : (
                        <Spacer />
                    )}
                </FlexBox>
            )}
        </React.Fragment>
    );
};

export default forwardRef<{ getFullData: () => DataResponse }, Props>(CustomDataTable);
